import React from 'react';
import { FiPrinter } from 'react-icons/fi';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import StatusPill from '../../components/ui/StatusPill';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Button from '../../components/ui/Button';

function Stat({ label, value, tone = 'default', emphasis = false }) {
  const toneClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'danger'
        ? 'text-danger'
        : tone === 'warning'
          ? 'text-amber-600'
          : 'text-ink';
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span
        className={`tabular-nums ${emphasis ? 'text-lg font-semibold' : 'text-sm font-medium'} ${toneClass}`}
      >
        {value}
      </span>
    </div>
  );
}

export default function InvoiceSummaryCard({ invoice, loading }) {
  if (loading) {
    return (
      <Card>
        <CardBody>
          <LoadingSpinner label="Loading invoice…" />
        </CardBody>
      </Card>
    );
  }
  if (!invoice) return null;

  const total = Number(invoice.totalAmount ?? invoice.amount ?? 0);
  const paid = Number(invoice.paidAmount ?? 0);
  const pending = Math.max(total - paid, 0);
  const code = invoice.invoiceNumber || `INV-${invoice.id}`;

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <div className="print:hidden">
        <Card>
          <CardHeader
            title={code}
            subtitle={
              <span className="flex items-center gap-2 text-xs text-ink-muted">
                <span>Customer #{invoice.customerId ?? '—'}</span>
                {invoice.dueDate && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>
                      Due <DateDisplay value={invoice.dueDate} />
                    </span>
                  </>
                )}
              </span>
            }
            actions={
              <div className="flex items-center gap-3">
                <StatusPill value={invoice.status} />
                <Button variant="ghost" size="sm" icon={FiPrinter} onClick={handlePrint}>
                  Print
                </Button>
              </div>
            }
          />
          <CardBody>
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
              <Stat
                label="Total"
                value={<CurrencyDisplay value={total} />}
                emphasis
              />
              <Stat
                label="Paid"
                value={<CurrencyDisplay value={paid} />}
                tone="success"
                emphasis
              />
              <Stat
                label="Pending"
                value={<CurrencyDisplay value={pending} />}
                tone={pending > 0 ? 'warning' : 'success'}
                emphasis
              />
              <Stat
                label="Issued"
                value={
                  invoice.issueDate ? (
                    <DateDisplay value={invoice.issueDate} />
                  ) : invoice.createdAt ? (
                    <DateDisplay value={invoice.createdAt} />
                  ) : (
                    <span className="text-ink-faint">—</span>
                  )
                }
              />
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="hidden print:block text-black p-8 bg-white max-w-4xl mx-auto">
        <div className="flex justify-between items-start border-b-2 border-gray-300 pb-6 mb-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900">INVOICE</h1>
            <div className="mt-2 text-sm text-gray-600">
              Invoice No: <span className="font-semibold text-gray-900">{code}</span>
            </div>
          </div>
          <div className="text-right">
            <h2 className="text-2xl font-bold text-gray-900">Orakris<span className="text-accent">.</span></h2>
            <div className="mt-1 text-sm text-gray-600">
              123 Finance Street<br />
              Business City, BC 12345<br />
              finance@orakris.com
            </div>
          </div>
        </div>

        <div className="flex justify-between mb-8">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-2">Billed To</h3>
            <div className="text-gray-900">
              Customer #{invoice.customerId ?? '—'}<br />
              [Customer Address Placeholder]<br />
              [Customer Contact]
            </div>
          </div>
          <div className="text-right">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <span className="text-gray-500">Date Issued:</span>
              <span className="font-medium text-gray-900">
                {invoice.issueDate || invoice.createdAt ? <DateDisplay value={invoice.issueDate || invoice.createdAt} /> : '—'}
              </span>
              <span className="text-gray-500">Due Date:</span>
              <span className="font-medium text-gray-900">
                {invoice.dueDate ? <DateDisplay value={invoice.dueDate} /> : '—'}
              </span>
              <span className="text-gray-500">Status:</span>
              <span className="font-medium text-gray-900 uppercase">{invoice.status}</span>
            </div>
          </div>
        </div>

        <table className="w-full text-left border-collapse mb-8">
          <thead>
            <tr className="border-b border-gray-300">
              <th className="py-3 font-semibold text-gray-900">Description</th>
              <th className="py-3 text-right font-semibold text-gray-900">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-200">
              <td className="py-4 text-gray-900">
                Services Rendered / Goods Provided
                {invoice.notes && <div className="text-sm text-gray-500 mt-1">{invoice.notes}</div>}
              </td>
              <td className="py-4 text-right text-gray-900"><CurrencyDisplay value={total} /></td>
            </tr>
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className="w-64 space-y-3">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Subtotal:</span>
              <span><CurrencyDisplay value={total} /></span>
            </div>
            <div className="flex justify-between text-sm text-gray-600 border-b border-gray-200 pb-3">
              <span>Paid:</span>
              <span><CurrencyDisplay value={paid} /></span>
            </div>
            <div className="flex justify-between text-lg font-bold text-gray-900 pt-1">
              <span>Balance Due:</span>
              <span><CurrencyDisplay value={pending} /></span>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center text-sm text-gray-500 pt-8 border-t border-gray-200">
          Thank you for your business. Please remit payment within the specified terms.
        </div>
      </div>
    </>
  );
}
